import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';

const PERSIST_USER_KEY = 'firebase_anonymous_user_id';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  authReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  const signIn = async (persistedUserId?: string): Promise<void> => {
    try {
      console.log('🔐 Attempting anonymous sign in...', { persistedUserId });
      
      // For emulator, we'll store the user ID manually since persistence doesn't work well
      const result = await signInAnonymously(auth);
      
      // Store user ID in localStorage for persistence
      localStorage.setItem(PERSIST_USER_KEY, result.user.uid);
      
      console.log('✅ Anonymous sign in successful:', result.user.uid);
    } catch (error: any) {
      console.error('❌ Error signing in anonymously:', error);
      
      // If it's a network error, the emulators might not be ready yet
      if (error.code === 'auth/network-request-failed') {
        console.log('🔄 Network failed, retrying in 2 seconds...');
        throw new Error('Network connection failed - emulators may not be ready');
      }
      
      throw error;
    }
  };

  useEffect(() => {
    console.log('🔥 AuthContext: Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔐 AuthContext: Auth state changed:', {
        user: user ? `User: ${user.uid}` : 'No user',
        isAnonymous: user?.isAnonymous || false,
        loading: loading,
        timestamp: new Date().toLocaleTimeString()
      });
      setUser(user);
      setLoading(false);
      setAuthReady(true);
    });

    return unsubscribe;
  }, []);

  // Auto sign in if not already authenticated
  useEffect(() => {
    if (authReady && !user) {
      console.log('🚀 AuthContext: Starting auto sign-in process...');
      
      // Check if we have a persisted user ID
      const persistedUserId = localStorage.getItem(PERSIST_USER_KEY);
      console.log('📱 Found persisted user ID:', persistedUserId);
      
      // Wait longer for emulators to be ready
      const timer = setTimeout(() => {
        signIn(persistedUserId || undefined).catch((error) => {
          console.error('❌ Auto sign-in failed:', error.message);
          
          // Retry with exponential backoff
          const retryDelays = [2000, 5000, 10000]; // 2s, 5s, 10s
          
          const attemptSignIn = (attempt: number) => {
            if (attempt >= retryDelays.length) {
              console.error('❌ All sign-in attempts failed. Please refresh the page.');
              return;
            }
            
            console.log(`🔄 Retry attempt ${attempt + 1} in ${retryDelays[attempt]}ms...`);
            setTimeout(() => {
              signIn().catch(() => attemptSignIn(attempt + 1));
            }, retryDelays[attempt]);
          };
          
          attemptSignIn(0);
        });
      }, 1000); // Wait 1 second initially
      
      return () => clearTimeout(timer);
    }
  }, [authReady, user]);

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    authReady
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};